import React, { forwardRef, useMemo } from 'react';
import { format } from 'date-fns';
import {
  groupSchedulesForExport,
  formatDayHeader,
  renderRestMarker,
  calculateAchievementRate,
  calculateDefectRate,
  computeDailyGroupSummaries,
  getColumnWidths,
} from '../../utils/scheduleExport';

const ScheduleExportImage = forwardRef(function ScheduleExportImage(
  { title, schedules, allSchedules, dates, workCenters, settledDateMap = {} },
  ref
) {
  const rowsByWorkCenter = useMemo(
    () =>
      groupSchedulesForExport({
        schedules,
        allSchedules,
        dates,
        workCenters,
      }).rowsByWorkCenter,
    [schedules, allSchedules, dates, workCenters]
  );

  const widths = getColumnWidths();
  const totalWidth =
    Object.values(widths).reduce((a, b) => a + b, 0) - widths.date + widths.date * dates.length;

  const cellStyle = (width, extra = {}) => ({
    width,
    minWidth: width,
    maxWidth: width,
    border: '1px solid #d1d5db',
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: '12px',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...extra,
  });

  const headerCellStyle = (width, extra = {}) =>
    cellStyle(width, {
      backgroundColor: '#f3f4f6',
      fontWeight: 600,
      color: '#374151',
      ...extra,
    });

  const labelCellStyle = (width) =>
    cellStyle(width, {
      textAlign: 'right',
      paddingRight: 8,
      color: '#b91c1c',
      fontWeight: 500,
    });

  const summaryValueStyle = (width) =>
    cellStyle(width, {
      color: '#b91c1c',
      fontWeight: 500,
    });

  const getDateCellMeta = (row, dateKey, workCenterName, settledMap) => {
    const plannedQty = row.dateMap[dateKey] || 0;
    const settlement = settledMap[dateKey];
    const items = settlement?.items;
    const isSettled =
      items &&
      items.some(
        (item) =>
          (item.work_center_name || '').trim() === workCenterName.trim()
      );

    if (isSettled) {
      const completedQty = items
        .filter((item) => row.scheduleIds.includes(item.schedule_object_id))
        .reduce((sum, item) => sum + (item.completed_quantity || 0), 0);
      const hasValue = plannedQty > 0 || completedQty > 0;
      let backgroundColor = 'transparent';
      if (hasValue) {
        if (completedQty >= plannedQty) {
          backgroundColor = '#bbf7d0'; // green-200
        } else if (plannedQty - completedQty < 5) {
          backgroundColor = '#fef08a'; // yellow-200
        } else {
          backgroundColor = '#fecaca'; // red-200
        }
      }
      return {
        display: hasValue ? `${completedQty} / ${plannedQty}` : '',
        style: { backgroundColor },
      };
    }

    return {
      display: renderRestMarker(null, plannedQty),
      style: {},
    };
  };

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: totalWidth,
        padding: 16,
        backgroundColor: '#fff',
        fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
        color: '#111827',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          fontSize: 22,
          fontWeight: 700,
          marginBottom: 12,
          letterSpacing: 2,
        }}
      >
        {title}
      </div>

      <table
        style={{
          width: totalWidth,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          border: '1px solid #d1d5db',
        }}
      >
        <thead>
          <tr>
            {[
              { label: '订单任务号', w: widths.orderTaskNumber },
              { label: '产品编号', w: widths.productCode },
              { label: '产品名称', w: widths.productName },
              { label: '产品规格', w: widths.specification },
              { label: '订单数', w: widths.orderQty },
              { label: '计划数', w: widths.plannedQty },
              { label: '累计生产', w: widths.cumulativeCompleted },
              { label: '订单计划', w: widths.orderPlan },
              { label: '下单日期', w: widths.orderDate },
              { label: '计划完成日期', w: widths.plannedEndDate },
              { label: '计划工时', w: widths.plannedHours },
              { label: '生产设备', w: widths.equipment },
            ].map((h) => (
              <th key={h.label} style={headerCellStyle(h.w)}>
                {h.label}
              </th>
            ))}
            {dates.map((date) => {
              const { date: d, weekday } = formatDayHeader(date);
              const day = date.getDay();
              const bg = day === 0 ? '#fed7aa' : day === 6 ? '#e5e7eb' : '#f3f4f6';
              return (
                <th key={d} style={headerCellStyle(widths.date, { backgroundColor: bg })}>
                  <div>{d}</div>
                  <div style={{ fontSize: 10, fontWeight: 400 }}>{weekday}</div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rowsByWorkCenter.map((group) => (
            <React.Fragment key={group.workCenter._id}>
              {group.rows.map((row, idx) => {
                const customStyle = row.isCustom
                  ? { color: '#dc2626', fontWeight: 500 }
                  : {};
                return (
                  <tr key={idx}>
                    <td style={cellStyle(widths.orderTaskNumber, customStyle)}>{row.orderTaskNumber}</td>
                    <td style={cellStyle(widths.productCode, customStyle)}>{row.productCode}</td>
                    <td style={cellStyle(widths.productName, customStyle)}>{row.productName}</td>
                    <td style={cellStyle(widths.specification, customStyle)}>{row.specification}</td>
                    <td style={cellStyle(widths.orderQty)}>{row.orderQty || ''}</td>
                    <td style={cellStyle(widths.plannedQty)}>{row.plannedQty || ''}</td>
                    <td style={cellStyle(widths.cumulativeCompleted)}>{row.cumulativeCompleted || ''}</td>
                    <td style={cellStyle(widths.orderPlan)}>{row.orderPlan || ''}</td>
                    <td style={cellStyle(widths.orderDate, customStyle)}>{row.orderDate}</td>
                    <td style={cellStyle(widths.plannedEndDate)}>{row.plannedEndDate}</td>
                    <td style={cellStyle(widths.plannedHours)}>{row.plannedHours || ''}</td>
                    <td style={cellStyle(widths.equipment, { backgroundColor: '#dbeafe' })}>
                      {row.equipment}
                    </td>
                    {dates.map((date) => {
                      const dk = format(date, 'yyyy-MM-dd');
                      const { display, style } = getDateCellMeta(
                        row,
                        dk,
                        group.workCenter.name,
                        settledDateMap
                      );
                      return (
                        <td key={dk} style={cellStyle(widths.date, style)}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* Summary rows for this equipment group */}
              {(() => {
                const dailySummaries = computeDailyGroupSummaries(
                  group.rows,
                  dates,
                  settledDateMap,
                  group.workCenter.name
                );
                return [
                  {
                    label: '实际完成',
                    getValue: (ds) => (ds.isSettled ? ds.totalCompleted : ds.totalCompleted || ''),
                  },
                  {
                    label: '计划达成率',
                    getValue: (ds) =>
                      ds.isSettled
                        ? calculateAchievementRate(ds.totalCompleted, ds.totalAssigned)
                        : '',
                  },
                  {
                    label: '不良数量',
                    getValue: (ds) => (ds.isSettled ? ds.totalScrapped : ds.totalScrapped || ''),
                  },
                  {
                    label: '不良率',
                    getValue: (ds) =>
                      ds.isSettled
                        ? calculateDefectRate(ds.totalScrapped, ds.totalCompleted)
                        : '',
                  },
                ].map((summary, idx) => (
                  <tr key={`summary-${group.workCenter._id}-${idx}`}>
                    <td colSpan={12} style={labelCellStyle(widths.orderTaskNumber + widths.productCode + widths.productName + widths.specification + widths.orderQty + widths.plannedQty + widths.cumulativeCompleted + widths.orderPlan + widths.orderDate + widths.plannedEndDate + widths.plannedHours + widths.equipment)}>
                      {summary.label}
                    </td>
                    {dates.map((date) => {
                      const dk = format(date, 'yyyy-MM-dd');
                      const ds = dailySummaries.find((s) => s.dateKey === dk);
                      const value = ds ? summary.getValue(ds) : '';
                      return (
                        <td key={dk} style={summaryValueStyle(widths.date)}>
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default ScheduleExportImage;
